import type { EventSettings } from '../services/events';

type RequestWindowSettings = Partial<EventSettings> | undefined;

export const CLOSE_COUNTDOWN_WINDOW_MS = 15 * 60 * 1000;

export function requestsCloseAtMsOf(settings: RequestWindowSettings): number | null {
  return typeof settings?.requestsCloseAtMs === 'number' ? settings.requestsCloseAtMs : null;
}

export function isClosedByShowStart(settings: RequestWindowSettings, nowMs: number): boolean {
  const closeAtMs = requestsCloseAtMsOf(settings);
  return settings?.requestsOpen !== false && closeAtMs !== null && nowMs >= closeAtMs;
}

export function areRequestsClosed(settings: RequestWindowSettings, nowMs: number): boolean {
  return settings?.requestsOpen === false || isClosedByShowStart(settings, nowMs);
}

function isVotingScheduledInFuture(settings: RequestWindowSettings, nowMs: number): boolean {
  if (!settings?.votingScheduledToOpen || !settings.votingOpenTime) return false;
  const openTimeMs = new Date(settings.votingOpenTime.replace('Z', '')).getTime();
  return nowMs < openTimeMs;
}

export function isVotingBlockedBySchedule(settings: RequestWindowSettings, nowMs: number): boolean {
  return settings?.votingClosed === true || isVotingScheduledInFuture(settings, nowMs);
}

export function isRequestsClosedForAttendee(settings: RequestWindowSettings, nowMs: number): boolean {
  return areRequestsClosed(settings, nowMs) || isVotingBlockedBySchedule(settings, nowMs);
}

export function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatClockTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export type DjRequestCloseStatus = {
  state: 'upcoming' | 'countdown' | 'closed' | 'reopened';
  text: string;
};

export function djRequestCloseStatus(
  settings: RequestWindowSettings,
  startMs: number | null,
  nowMs: number,
): DjRequestCloseStatus | null {
  if (settings?.closeRequestsAtStart !== true) return null;
  const closeAtMs = requestsCloseAtMsOf(settings);
  if (closeAtMs !== null) {
    if (nowMs >= closeAtMs) return { state: 'closed', text: `Requests closed automatically at ${formatClockTime(closeAtMs)}` };
    const remainingMs = closeAtMs - nowMs;
    if (remainingMs <= CLOSE_COUNTDOWN_WINDOW_MS) return { state: 'countdown', text: `Requests close in ${formatCountdown(remainingMs)}` };
    return { state: 'upcoming', text: `Requests close automatically at ${formatClockTime(closeAtMs)}` };
  }
  if (startMs !== null && nowMs >= startMs) return { state: 'reopened', text: `Reopened after the ${formatClockTime(startMs)} close` };
  return null;
}

export type AttendeeVotingStyle = 'rank' | 'vote' | 'none';

function stillOpenActivity(style: AttendeeVotingStyle): string | null {
  if (style === 'rank') return 'rank and vote on the queue';
  if (style === 'vote') return 'vote on the queue';
  return null;
}

export function requestsJustClosedMessage(style: AttendeeVotingStyle): string {
  const activity = stillOpenActivity(style);
  return activity ? `Requests just closed. You can still ${activity}.` : 'Requests just closed.';
}

export function requestsClosedBannerMessage(style: AttendeeVotingStyle): string {
  const activity = stillOpenActivity(style);
  return activity ? `Song requests are closed — you can still ${activity}!` : 'Song requests are closed';
}

export function nextRequestsCloseAtMs(input: {
  closeAtStart: boolean;
  startMs: number | null;
  previous: { closeAtStart: boolean; startMs: number | null; closeAtMs: number | null };
}): number | null {
  const { closeAtStart, startMs, previous } = input;
  if (!closeAtStart || startMs === null) return null;
  if (closeAtStart !== previous.closeAtStart || startMs !== previous.startMs) return startMs;
  return previous.closeAtMs;
}
